import Framework, { shapes } from '@greenville/framework';
import { Box, Button, withStyles } from '@material-ui/core';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { Stack } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import DialogComponent from '../../../../../common/components/Dialog';
import TableComponent from '../../../../../common/components/Table';
import ColumnConfig from '../../../../../common/config/ColumnConfig';
import * as constants from '../../../../../common/constants';
import utils from '../../../../../common/utils';
import ExportToCSV from '../../../common/components/ExportToCSV';
import ChannelsModel from '../../../models/ChannelsModel';
import ChatgptUtilityIESUser from '../../../models/ChatgptIESUser';
import ChatgptUtilityMapping from '../../../models/ChatgptMapping';
import InternalUsersList from '../../../models/InternalUsersList';
import SubTypeResponse from '../../../models/SubTypeModel';
import TenantConfigModel from '../../../models/TenantConfig';

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = () => ({
  buttonStyle: {
    cursor: 'pointer',
    width: '170px',
    margin: '10px',
    fontSize: '14px',
    background: '#005d83 !important'
  }
});

/* eslint-disable no-shadow */
function ChannelsDataGrid(props) {
  const {
    channelsUtilityData,
    handleAscending,
    ChatgptUtilityIESUserData,
    classes,
    chatgptMappingUtility,
    isNextPageKey,
    isNextPageKeyAudit,
    handleSearch,
    channelsSubType,
    channelsModelData,
    modelStatus,
    setUserId,
    userIdChecked,
    isUserIdExcluded,
    internalUsers,
    isUserFeedBack,
    tenantConfig
  } = props;
  const [subTypeData, setSubTypeData] = useState([]);
  const [filteredArray, setFilteredArray] = useState([]);
  const [order, SetOrder] = useState('ASC');
  const [directionValue, setDirectionValue] = useState('');
  const [userEmailDetails, setUserEmailDetails] = useState({});
  const [selectedValue, setSelectedValue] = useState('');
  const [openChat, openChatDialog] = useState(false);
  const [dataValue, setDataValue] = useState({});
  const [userTocDetails, setUserTocDetails] = useState({});
  const [dataLength, setDatalength] = useState(0);
  const [modalIndex, setModalIndex] = useState(0);
  const [configData, setConfigData] = useState({ hideUserColumn: false, whetherFetchUserData: false });
  const loadingStatus = modelStatus.isPending;
  const isErrorValue =
    modelStatus.isError && modelStatus.error && modelStatus.error.status === 401 && modelStatus.error.status === 500;
  const errorDisplayVal = modelStatus.error;
  const loaderParams = {
    loadingStatus,
    isErrorValue,
    errorDisplayVal
  };
  const internalUserIds = JSON.parse(JSON.stringify(toJS(internalUsers)));
  const setTenantSpecificConfig = () => {
    const { data } = toJS(tenantConfig);
    if (data.length > 0) {
      const match = data.find((config) => config.name === 'channels');
      if (match) {
        setConfigData({ hideUserColumn: match.hideUserId, whetherFetchUserData: match.whetherCallIESUser });
      }
    }
  };

  const handleEvent = (params, index) => {
    const { userId, type, requestId, channelsId } = params;
    if (isUserFeedBack) {
      setDataValue({ ...dataValue, ...params });
    }
    channelsModelData.fetch({ userId, type, requestId, channelsId });
    setModalIndex(index);
    setSelectedValue(index);
    openChatDialog(true);
  };

  useEffect(() => {
    // Function to check URL parameters
    const checkUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const channelsId = urlParams.get('channelsId');
      const requestId = urlParams.get('requestId');
      const type = urlParams.get('type');
      const userId = urlParams.get('userId');

      if (channelsId && requestId && type && userId) {
        setDatalength(1);
        handleEvent({ channelsId, requestId, type, userId }, 0);
      }
    };
    if (!isUserFeedBack) {
      checkUrlParams();
    }
  }, []);

  useEffect(() => {
    setTenantSpecificConfig();
    const data = Array.isArray(channelsUtilityData) ? channelsUtilityData : [];
    if (data.length > 0 && isUserIdExcluded && internalUserIds?.userId.length > 0) {
      const filteredData = data?.filter((item) => !internalUserIds?.userId.includes(item.userId));
      setFilteredArray(filteredData);
      setDatalength(filteredData.length);
    } else {
      setFilteredArray(data);
      setDatalength(data.length);
    }
    /* eslint-disable camelcase */
    Framework.getEventManager().on(constants.SET_CHATGPT_IES_USER_DATA, () => {
      const { userData } = ChatgptUtilityIESUserData;
      if (userData.length > 0) {
        setUserEmailDetails(userData[userData.length - 1]);
      }
    });
    Framework.getEventManager().on(constants.SET_CHATGPT_MAPPING_DATA, () => {
      const { toc } = chatgptMappingUtility;
      if (toc.length > 0) {
        setUserTocDetails(toc);
      }
    });
    Framework.getEventManager().on(constants.CHANNELS_SUB_TYPE_RESPONSE, () => {
      const resData = utils.processChannelsSubTypeResponse(channelsSubType);
      setSubTypeData(resData);
    });
    Framework.getEventManager().on(constants.CHANNELS_MODEL_RESPONSE, () => {
      const resData = utils.processChannelsSubTypeResponse(channelsModelData);
      if (resData.length > 0) {
        const [record] = resData;
        setDataValue({ ...dataValue, ...record });
      }
    });
  }, [channelsUtilityData, ChatgptUtilityIESUserData, userTocDetails, isUserIdExcluded]);

  useEffect(() => {
    if (dataValue?.channelsId && dataValue?.requestId && !isUserFeedBack) {
      const typeValue = dataValue?.type;
      const url = `${window.location.origin}${window.location.pathname}?channelsId=${dataValue.channelsId}&requestId=${dataValue.requestId}&type=${typeValue}&userId=${dataValue.userId}`;
      // window.location.href = url;
      window.history.replaceState({}, '', url);
    }
  }, [dataValue]);

  /* eslint-disable no-param-reassign */

  const getLastkey = (isNextPageKey) => {
    let lastKey = {};
    const allKeysEmpty = Object.values(isNextPageKey.ExclusiveStartKey).every((value) => !value);
    if (isNextPageKey && isNextPageKey.ExclusiveStartKey && !allKeysEmpty) {
      const startKey = isNextPageKey.ExclusiveStartKey;
      lastKey = Object.entries(startKey).reduce((obj, [key, value]) => {
        if (key !== null && value !== null) {
          obj[key] = value;
        }
        return obj;
      }, {});
      return { ExclusiveStartKey: lastKey };
    }
    return lastKey;
  };
  const handlePageChange = (e) => {
    e.preventDefault();
    const { isNextPageKey, isNextPageKeyAudit } = props;
    const lastKey = getLastkey(isNextPageKey);
    const lastKeyAudit = getLastkey(isNextPageKeyAudit);
    handleSearch({ lastKey, lastKeyAudit });
  };

  const handleClose = () => {
    if (!isUserFeedBack) {
      const url = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, '', url);
    }
    openChatDialog(false);
    setModalIndex(0);
    setSelectedValue(modalIndex);
    setSubTypeData([]);
    setDataValue({});
  };

  const handleModalChange = (value) => {
    let modalArrData = channelsUtilityData;
    if (isUserIdExcluded && internalUserIds?.userId.length > 0) {
      modalArrData = filteredArray;
    }
    if (value === 'prev' && modalIndex > 0) {
      const { userId, type, requestId, channelsId } = modalArrData[modalIndex - 1];
      if (isUserFeedBack) {
        setDataValue({ ...dataValue, ...modalArrData[modalIndex - 1] });
      }
      channelsModelData.fetch({ userId, type, requestId, channelsId });
      setModalIndex(modalIndex - 1);
      setSubTypeData([]);
    } else {
      const { userId, type, requestId, channelsId } = modalArrData[modalIndex + 1];
      if (isUserFeedBack) {
        setDataValue({ ...dataValue, ...modalArrData[modalIndex + 1] });
      }
      channelsModelData.fetch({ userId, type, requestId, channelsId });
      setModalIndex(modalIndex + 1);
      setSubTypeData([]);
    }
  };

  const sorting = (value) => {
    if (order === 'ASC') {
      handleAscending('asc', value);
      SetOrder('DSC');
      setDirectionValue(value);
    }
    if (order === 'DSC') {
      handleAscending('des', value);
      SetOrder('ASC');
      setDirectionValue(value);
    }
  };

  const handleGETUserId = (userId) => {
    const { userData } = ChatgptUtilityIESUserData;
    if (userId) {
      const dataMatch = userData && userData.find((data) => data.userId === userId);
      if (dataMatch) {
        setUserEmailDetails(dataMatch);
      } else {
        ChatgptUtilityIESUserData.fetch({ userId });
      }
    }
  };

  const handleSelectedCheckBox = (value, sourceType) => {
    const { userId, requestId } = dataValue;
    if (subTypeData.length > 0) {
      const findValue = subTypeData.find((data) => data.requestId === requestId && data.type === sourceType);
      if (!findValue) {
        channelsSubType.fetch({ userId, type: value, requestId });
      }
    } else {
      channelsSubType.fetch({ userId, type: value, requestId });
    }
  };

  const onColumnClick = (val) => {
    sorting(val);
  };
  const onRowClick = (val, index) => {
    handleEvent(val, index);
  };
  const onMouseEnter = (val) => {
    handleGETUserId(val);
  };

  return (
    <>
      {!openChat && filteredArray && filteredArray.length > 0 && (
        <>
          <ExportToCSV
            headers={
              isUserFeedBack
                ? ColumnConfig.CommonFeedbackColumn({
                    bookId: 'Channels Id',
                    hideUserColumn: configData?.hideUserColumn
                  })
                : [
                    ...ColumnConfig.ChannelsDataGridColumn(configData?.hideUserColumn),
                    { key: 'requestId', name: constants.REQUEST_ID }
                  ]
            }
            data={filteredArray}
            fileTitle={!isUserFeedBack ? 'ChannelsAuditLogs' : 'ChannelsUserFeedbackLogs'}
          />
          <TableComponent
            columns={
              isUserFeedBack
                ? ColumnConfig.CommonFeedbackColumn({
                    bookId: 'Channels Id',
                    hideUserColumn: configData?.hideUserColumn
                  })
                : ColumnConfig.ChannelsDataGridColumn(configData?.hideUserColumn)
            }
            data={filteredArray}
            onColumnClick={onColumnClick}
            order={order}
            onRowClick={onRowClick}
            selectedValue={selectedValue}
            directionValue={directionValue}
            userEmailDetails={userEmailDetails}
            onMouseEnter={onMouseEnter}
            setUserId={setUserId}
            userIdChecked={userIdChecked}
            configData={configData}
          />
          <br />
          <Box spacing={2}>
            <Stack sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row' }}>
              {((isNextPageKey &&
                isNextPageKey.ExclusiveStartKey &&
                isNextPageKey.ExclusiveStartKey.userId &&
                isNextPageKey.ExclusiveStartKey.userId.S !== null) ||
                (isNextPageKey &&
                  isNextPageKey.ExclusiveStartKey &&
                  isNextPageKey.ExclusiveStartKey.feedbackId &&
                  isNextPageKey.ExclusiveStartKey.feedbackId.S !== null) ||
                (isNextPageKeyAudit &&
                  isNextPageKeyAudit.ExclusiveStartKey &&
                  isNextPageKeyAudit.ExclusiveStartKey.bookId &&
                  isNextPageKeyAudit.ExclusiveStartKey.bookId.S !== null)) && (
                <Box>
                  <ThemeProvider theme={themes}>
                    <Button
                      size="small"
                      color="primary"
                      className={classes.buttonStyle}
                      variant="contained"
                      onClick={(e) => handlePageChange(e)}
                    >
                      <Tooltip title="Load next set of data">Load More</Tooltip>
                    </Button>
                  </ThemeProvider>
                </Box>
              )}
            </Stack>
          </Box>
        </>
      )}

      {openChat && (
        <DialogComponent
          openChat={openChat}
          dataValue={dataValue}
          userTocDetails={userTocDetails}
          dataLength={dataLength}
          modalIndex={modalIndex}
          handleClose={handleClose}
          handleModalChange={handleModalChange}
          subTypeData={subTypeData}
          handleSelectedCheckBox={handleSelectedCheckBox}
          loaderParams={loaderParams}
          isFeedback={isUserFeedBack}
        />
      )}
    </>
  );
}

ChannelsDataGrid.propTypes = {
  handleSearch: PropTypes.func.isRequired,
  channelsUtilityData: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  handleAscending: PropTypes.func.isRequired,
  ChatgptUtilityIESUserData: shapes.modelOf(ChatgptUtilityIESUser).isRequired,
  chatgptMappingUtility: shapes.modelOf(ChatgptUtilityMapping).isRequired,
  isNextPageKey: PropTypes.object,
  isNextPageKeyAudit: PropTypes.object,
  channelsSubType: shapes.modelOf(SubTypeResponse),
  channelsModelData: shapes.modelOf(ChannelsModel),
  modelStatus: shapes.state.isRequired,
  userIdChecked: PropTypes.bool.isRequired,
  setUserId: PropTypes.func.isRequired,
  isUserIdExcluded: PropTypes.bool.isRequired,
  internalUsers: shapes.modelOf(InternalUsersList).isRequired,
  isUserFeedBack: PropTypes.bool,
  tenantConfig: shapes.modelOf(TenantConfigModel).isRequired
};

ChannelsDataGrid.defaultProps = {
  isNextPageKey: {},
  isNextPageKeyAudit: {},
  channelsSubType: [],
  channelsModelData: [],
  isUserFeedBack: false
};

export default withStyles(styles)(
  observer(
    inject(
      'ChatgptUtilityIESUserData',
      'chatgptMappingUtility',
      'channelsSubType',
      'channelsModelData',
      'modelStatus',
      'internalUsers',
      'tenantConfig'
    )(ChannelsDataGrid)
  )
);
